import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip } from 'react-leaflet';
import { Box, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';

import { fetchZipSubmissionGeometry } from '@src/api/actions/dashboard/zipSubmissions';

const ADD_COLOR = '#2e7d32';
const REMOVE_COLOR = '#d32f2f';

// Adds render green, removes red. When submissionIds are provided we fetch
// the actual ZIP boundary outlines on demand (one request even for a
// multi-territory batch; never in the list payload — too heavy) and shade
// them; ZIPs without a stored shape, or oversized batches the server
// truncates, fall back to centroid dots.
export default function SubmissionDiffMap({ items = [], submissionIds = [], height = 220 }) {
  const [geometryByZip, setGeometryByZip] = useState(null);

  const idsKey = (Array.isArray(submissionIds) ? submissionIds : [submissionIds])
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    if (!idsKey) {
      setGeometryByZip(null);
      return undefined;
    }
    let isMounted = true;
    setGeometryByZip(null);
    fetchZipSubmissionGeometry(idsKey.split(','))
      .then((res) => {
        if (!isMounted) return;
        const map = {};
        (res.data?.items || []).forEach((item) => {
          if (item.geometry) map[item.zip] = item.geometry;
        });
        setGeometryByZip(map);
      })
      .catch(() => {
        // Geometry is progressive enhancement — dots still render without it.
        if (isMounted) setGeometryByZip({});
      });
    return () => { isMounted = false; };
  }, [idsKey]);

  const geoItems = useMemo(
    () => items.filter((i) => i.latitude != null && i.longitude != null),
    [items],
  );

  const polygonFeatures = useMemo(() => {
    if (!geometryByZip) return { type: 'FeatureCollection', features: [] };
    const features = [];
    items.forEach((item) => {
      const zip = item.zip || item.zip_raw;
      const geometry = geometryByZip[zip];
      if (!geometry) return;
      features.push({
        type: 'Feature',
        geometry,
        properties: { zip, action: item.action, city: item.city || '' },
      });
    });
    return { type: 'FeatureCollection', features };
  }, [items, geometryByZip]);

  const zipsWithPolygons = useMemo(
    () => new Set(polygonFeatures.features.map((f) => f.properties.zip)),
    [polygonFeatures],
  );

  const bounds = useMemo(() => {
    if (geoItems.length === 0) return null;
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    geoItems.forEach((i) => {
      if (i.latitude < minLat) minLat = i.latitude;
      if (i.latitude > maxLat) maxLat = i.latitude;
      if (i.longitude < minLng) minLng = i.longitude;
      if (i.longitude > maxLng) maxLng = i.longitude;
    });
    const pad = 0.02;
    return [
      [minLat - pad, minLng - pad],
      [maxLat + pad, maxLng + pad],
    ];
  }, [geoItems]);

  if (!geoItems.length || !bounds) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No geometry available for this submission.
        </Typography>
      </Box>
    );
  }

  const polygonStyle = (feature = {}) => {
    const action = feature.properties?.action;
    const color = action === 'add' ? ADD_COLOR : REMOVE_COLOR;
    return {
      color,
      weight: 1.5,
      fillColor: color,
      fillOpacity: action === 'add' ? 0.35 : 0.25,
      dashArray: action === 'remove' ? '4 3' : undefined,
    };
  };

  const handleEachFeature = (feature, layer) => {
    const props = feature?.properties || {};
    layer.bindTooltip(
      `${props.action === 'add' ? '+ ' : '− '}${props.zip}${props.city ? ` · ${props.city}` : ''}`,
      { sticky: true },
    );
  };

  return (
    <Box sx={{ height, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [10, 10] }}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        attributionControl={false}
        preferCanvas
        maxZoom={14}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains={['a', 'b', 'c', 'd']}
          maxZoom={14}
          keepBuffer={1}
          updateWhenIdle
          updateWhenZooming={false}
        />
        {polygonFeatures.features.length > 0 && (
          <GeoJSON
            key={`diff-polygons-${idsKey}-${polygonFeatures.features.length}`}
            data={polygonFeatures}
            style={polygonStyle}
            onEachFeature={handleEachFeature}
          />
        )}
        {geoItems
          .filter((item) => !zipsWithPolygons.has(item.zip || item.zip_raw))
          .map((item) => {
            const color = item.action === 'add' ? ADD_COLOR : REMOVE_COLOR;
            return (
              <CircleMarker
                key={item.id}
                center={[item.latitude, item.longitude]}
                radius={5}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: 0.7,
                  weight: 1.5,
                }}
              >
                <Tooltip direction="top" offset={[0, -4]}>
                  <span>
                    {item.action === 'add' ? '+ ' : '− '}
                    {item.zip || item.zip_raw}
                    {item.city ? ` · ${item.city}` : ''}
                  </span>
                </Tooltip>
              </CircleMarker>
            );
          })}
      </MapContainer>
    </Box>
  );
}
